import { useEffect } from 'react';
import { getAppDomain, SERVICE_NATION_BY_EVER_PRO_DOMAIN } from '../../config/domains';
import { getServiceNationDestinationUrl } from '../utils/serviceNationLinks';
import { NavigationTarget, useNavigationUtils } from '../utils/useNavigationUtils';

/**
 * Intercepts clicks on any `<a href>` to a Service Nation page that the app does not open itself
 * (hosted article HTML, Chameleon experiences, etc.) and opens it with `openExternalLink`, which
 * signs the member in to SN through `/edge-auth`. Links opened from code (content cards, partner
 * resources) already go through `openExternalLink` and need nothing here.
 *
 * The click is marked with `preventDefault()`. A React `onClick` on the same link runs
 * after this listener and must skip its own navigation when `event.defaultPrevented` is set.
 *
 * Modified clicks (Cmd/Ctrl/Shift/Alt) and middle clicks are left to the browser, so they
 * keep their native behavior (background tab, new window, download) but open without a token.
 */
export const useServiceNationLinkInterceptor = () => {
  const { openExternalLink } = useNavigationUtils();
  const isServiceNationApp = getAppDomain() === SERVICE_NATION_BY_EVER_PRO_DOMAIN;

  useEffect(() => {
    if (!isServiceNationApp) return undefined;

    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || !getServiceNationDestinationUrl(anchor.href)) return;

      event.preventDefault();
      openExternalLink(
        anchor.href,
        anchor.target === '_blank' ? NavigationTarget.Browser : NavigationTarget.Webview,
      );
    };

    // Capture phase so links rendered outside React (articles, Chameleon) are caught too
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isServiceNationApp, openExternalLink]);
};
